import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import type { ClassOffering } from '../../api/classes';
import { createClassOffering, updateClassOffering, getClassSeries, endClassSeries } from '../../api/classes';
import { uploadImage, getImageUrlById } from '../../api/images';
import ClassDescriptionEditor from './ClassDescriptionEditor';
import { RecurrenceEditor } from '../../shared/ui';
import type { RecurrenceRule } from '../../shared/types';

function useBlobUrls(files: File[]): string[] {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const list = files.map((f) => URL.createObjectURL(f));
    setUrls(list);
    return () => list.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);
  return urls;
}

interface ClassOfferingModalProps {
  show: boolean;
  onHide: () => void;
  onSaved: () => void;
  offering: ClassOffering | null;
}

const ClassOfferingModal: React.FC<ClassOfferingModalProps> = ({ show, onHide, onSaved, offering }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isPublic, setIsPublic] = useState(false);
  const [imageIds, setImageIds] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [sessionStartDate, setSessionStartDate] = useState('');
  const [sessionTimeStart, setSessionTimeStart] = useState('19:00');
  const [sessionTimeEnd, setSessionTimeEnd] = useState('21:00');
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule>({
    frequency: 'Weekly',
    interval: 1,
    byWeekday: ['Mon'],
    ends: { kind: 'never' },
  });
  const [showEndSeries, setShowEndSeries] = useState(false);
  const [seriesEndDate, setSeriesEndDate] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const newFilePreviewUrls = useBlobUrls(newFiles);

  const isEdit = !!offering;

  useEffect(() => {
    if (offering) {
      setTitle(offering.title);
      setSubtitle(offering.subtitle ?? '');
      setDescriptionHtml(offering.descriptionHtml ?? '');
      setDateRangeStart(offering.dateRangeStart ? new Date(offering.dateRangeStart).toISOString().split('T')[0] : '');
      setDateRangeEnd(offering.dateRangeEnd ? new Date(offering.dateRangeEnd).toISOString().split('T')[0] : '');
      setDisplayOrder(offering.displayOrder);
      setIsPublic(offering.isPublic);
      setImageIds(offering.imageIds ?? []);
      setNewFiles([]);
      setIsRecurring(false);
      setSessionStartDate('');
      setShowEndSeries(false);
      setSeriesEndDate('');

      // Load saved recurrence config (if any) so edits don't "lose" recurrence details.
      getClassSeries(offering.id)
        .then((series) => {
          if (!series) {
            setIsRecurring(false);
            return;
          }
          setIsRecurring(true);
          setSessionStartDate(series.startDate);
          setSessionTimeStart(series.timeStart);
          setSessionTimeEnd(series.timeEnd);
          setRecurrenceRule(series.recurrence);
          setSeriesEndDate(series.endDate ?? '');
        })
        .catch(() => {
          // ignore; recurrence is optional
        });
    } else {
      setTitle('');
      setSubtitle('');
      setDescriptionHtml('');
      setDateRangeStart('');
      setDateRangeEnd('');
      setDisplayOrder(0);
      setIsPublic(false);
      setImageIds([]);
      setNewFiles([]);
      setIsRecurring(false);
      setSessionStartDate('');
      setShowEndSeries(false);
      setSeriesEndDate('');
    }
    setError(null);
  }, [offering, show]);

  const removeImage = (kind: 'id' | 'file', index: number) => {
    if (kind === 'id') setImageIds((prev) => prev.filter((_, i) => i !== index));
    else setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) { setError('Title is required'); return; }
    if (isRecurring) {
      if (!sessionStartDate.trim()) { setError('Session start date is required for recurring classes'); return; }
      if (!sessionTimeStart.trim() || !sessionTimeEnd.trim()) { setError('Session start/end time is required'); return; }
    }
    setIsLoading(true);
    try {
      const uploadedIds: string[] = [];
      for (const file of newFiles) {
        const up = await uploadImage(file);
        uploadedIds.push(up.id);
      }
      const finalImageIds = [...imageIds, ...uploadedIds];
      const htmlToSave = descriptionHtml;
      const cleanedRecurrence: RecurrenceRule | undefined = isRecurring
        ? {
            frequency: 'Weekly',
            interval: 1,
            byWeekday: recurrenceRule.byWeekday?.length ? recurrenceRule.byWeekday : ['Mon'],
            ends: { kind: 'never' },
          }
        : undefined;
      const payload = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        descriptionHtml: DOMPurify.sanitize(htmlToSave) || null,
        dateRangeStart: dateRangeStart.trim() || null,
        dateRangeEnd: dateRangeEnd.trim() || null,
        displayOrder,
        isPublic,
        imageIds: finalImageIds,
        ...(isRecurring
          ? {
              sessionStartDate: sessionStartDate.trim(),
              sessionTimeStart: sessionTimeStart.trim(),
              sessionTimeEnd: sessionTimeEnd.trim(),
              recurrence: cleanedRecurrence,
            }
          : {}),
      };
      if (isEdit && offering) {
        await updateClassOffering(offering.id, payload);
      } else {
        await createClassOffering(payload);
      }
      onSaved();
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-journal-text me-2" aria-hidden />
              {isEdit ? 'Edit Class' : 'Add Class'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={isLoading} aria-label="Close" />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2" />{error}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Title *</label>
                <input type="text" className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} required />
              </div>
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="classRecurring"
                    checked={isRecurring}
                    disabled={isEdit && isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="classRecurring">
                    <i className="bi bi-arrow-repeat me-2" aria-hidden />
                    Recurring sessions
                  </label>
                </div>
                {isRecurring && (
                  <div className="mt-2">
                    <div className="row g-2">
                      <div className="col-12 col-md-4">
                        <label className="form-label mb-1">Session start date</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={sessionStartDate}
                          onChange={(e) => setSessionStartDate(e.target.value)}
                        />
                      </div>
                      <div className="col-6 col-md-4">
                        <label className="form-label mb-1">Start time</label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={sessionTimeStart}
                          onChange={(e) => setSessionTimeStart(e.target.value)}
                        />
                      </div>
                      <div className="col-6 col-md-4">
                        <label className="form-label mb-1">End time</label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={sessionTimeEnd}
                          onChange={(e) => setSessionTimeEnd(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <RecurrenceEditor value={recurrenceRule} onChange={setRecurrenceRule} disabled={isLoading} />
                    </div>

                    {isEdit && offering && (
                      <div className="mt-3">
                        {!showEndSeries ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={isLoading}
                            onClick={() => setShowEndSeries(true)}
                          >
                            <i className="bi bi-stop-circle me-2" aria-hidden />
                            End recurring schedule
                          </button>
                        ) : (
                          <div className="border rounded p-2 bg-white">
                            <div className="row g-2 align-items-end">
                              <div className="col-12 col-md-6">
                                <label className="form-label mb-1">End date (inclusive)</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  value={seriesEndDate}
                                  onChange={(e) => setSeriesEndDate(e.target.value)}
                                  disabled={isLoading}
                                />
                              </div>
                              <div className="col-12 col-md-6 d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  disabled={isLoading || !seriesEndDate.trim()}
                                  onClick={async () => {
                                    setIsLoading(true);
                                    setError(null);
                                    try {
                                      await endClassSeries(offering.id, seriesEndDate.trim());
                                      setShowEndSeries(false);
                                      onSaved();
                                    } catch (err) {
                                      setError(err instanceof Error ? err.message : 'Failed to end series');
                                    } finally {
                                      setIsLoading(false);
                                    }
                                  }}
                                >
                                  End
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  disabled={isLoading}
                                  onClick={() => setShowEndSeries(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Subtitle</label>
                <input type="text" className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Optional" />
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Course date range start (optional)</label>
                    <input type="date" className="form-control" value={dateRangeStart} onChange={(e) => setDateRangeStart(e.target.value)} />
                    {isRecurring && (
                      <small className="text-muted d-block mt-1">
                        This is the overall course duration (optional). The recurring schedule is defined above.
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Course date range end (optional)</label>
                    <input type="date" className="form-control" value={dateRangeEnd} onChange={(e) => setDateRangeEnd(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <ClassDescriptionEditor
                  key={offering?.id ?? 'new'}
                  html={descriptionHtml}
                  onHtmlChange={setDescriptionHtml}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Images</label>
                <p className="text-muted small mb-2">First image is used as the cover on the Classes page. You can add multiple; they will show in the expanded view.</p>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {imageIds.map((id, idx) => (
                    <div key={id} className="position-relative">
                      <img src={getImageUrlById(id)} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                      <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1" style={{ padding: '0.1rem 0.35rem' }} onClick={() => removeImage('id', idx)} aria-label="Remove">×</button>
                    </div>
                  ))}
                  {newFiles.map((_, idx) => (
                    <div key={`file-${idx}`} className="position-relative">
                      <img src={newFilePreviewUrls[idx]} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                      <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1" style={{ padding: '0.1rem 0.35rem' }} onClick={() => removeImage('file', idx)} aria-label="Remove">×</button>
                    </div>
                  ))}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control"
                  accept="image/png,image/jpeg,image/jpg"
                  multiple
                  onChange={(e) => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    if (files.length) setNewFiles((prev) => [...prev, ...files]);
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Display order</label>
                    <input type="number" className="form-control" value={displayOrder} onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)} min={0} />
                  </div>
                </div>
                <div className="col-md-6 d-flex align-items-end pb-2">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="isPublic" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                    <label className="form-check-label" htmlFor="isPublic">Public (show on Classes page)</label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</> : isEdit ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClassOfferingModal;
